import { useState, useRef, useEffect } from 'react';

export default function ChatPanel({ messages, onSend, playerName, opponentName }) {
  const [input, setInput] = useState('');
  const [rejected, setRejected] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  useEffect(() => {
    if (rejected) {
      const t = setTimeout(() => setRejected(null), 2000);
      return () => clearTimeout(t);
    }
  }, [rejected]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    if (trimmed.length > 150) {
      setRejected('Message too long (max 150 chars)');
      return;
    }
    onSend(trimmed);
    setInput('');
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (ts) => {
    const d = new Date(ts);
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.header}>
        <span style={styles.headerLabel}>💬 Chat</span>
        <span style={styles.headerSub}>Not saved · Vanishes after match</span>
      </div>

      {/* Messages */}
      <div style={styles.messages}>
        {messages.length === 0 && (
          <p style={styles.emptyMsg}>Say something to your opponent...</p>
        )}
        {messages.map((msg, i) => {
          const isMe = msg.from === 'you';
          return (
            <div
              key={i}
              style={{ ...styles.msgRow, justifyContent: isMe ? 'flex-end' : 'flex-start' }}
            >
              <div
                style={{
                  ...styles.bubble,
                  background: isMe
                    ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
                    : 'rgba(255,255,255,0.08)',
                  borderRadius: isMe ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                }}
              >
                {!isMe && (
                  <span style={styles.senderName}>{msg.sender_name || opponentName}</span>
                )}
                <span style={styles.msgText}>{msg.message}</span>
                <span style={styles.msgTime}>{formatTime(msg.timestamp)}</span>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Error */}
      {rejected && <p style={styles.error}>{rejected}</p>}

      {/* Input */}
      <div style={styles.inputRow}>
        <input
          style={styles.input}
          value={input}
          onChange={(e) => setInput(e.target.value.slice(0, 150))}
          onKeyDown={handleKey}
          placeholder="Message..."
          maxLength={150}
        />
        <span style={styles.charCount}>{input.length}/150</span>
        <button
          style={{
            ...styles.sendBtn,
            opacity: input.trim() ? 1 : 0.4,
          }}
          onClick={handleSend}
          disabled={!input.trim()}
        >
          ↑
        </button>
      </div>
    </div>
  );
}

const styles = {
  wrapper: {
    display: 'flex', flexDirection: 'column',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: '16px', overflow: 'hidden',
    height: '300px',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 14px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  headerLabel: { fontSize: '13px', fontWeight: '600', color: 'rgba(255,255,255,0.7)' },
  headerSub: { fontSize: '10px', color: 'rgba(255,255,255,0.25)' },
  messages: {
    flex: 1, overflowY: 'auto', padding: '12px',
    display: 'flex', flexDirection: 'column', gap: '8px',
  },
  emptyMsg: {
    textAlign: 'center', color: 'rgba(255,255,255,0.2)',
    fontSize: '12px', margin: 'auto 0',
  },
  msgRow: { display: 'flex' },
  bubble: {
    maxWidth: '80%', padding: '8px 12px',
    display: 'flex', flexDirection: 'column', gap: '2px',
  },
  senderName: { fontSize: '10px', color: 'rgba(255,255,255,0.4)', fontWeight: '600' },
  msgText: { fontSize: '13px', color: '#fff', lineHeight: '1.4', wordBreak: 'break-word' },
  msgTime: { fontSize: '10px', color: 'rgba(255,255,255,0.3)', alignSelf: 'flex-end' },
  error: {
    margin: '0 12px 4px', padding: '6px 10px',
    background: 'rgba(239,68,68,0.15)', borderRadius: '8px',
    fontSize: '12px', color: '#ef4444',
  },
  inputRow: {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '10px 12px', borderTop: '1px solid rgba(255,255,255,0.06)',
  },
  input: {
    flex: 1, background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px',
    padding: '8px 12px', color: '#fff', fontSize: '13px', outline: 'none',
  },
  charCount: { fontSize: '10px', color: 'rgba(255,255,255,0.25)', minWidth: '30px' },
  sendBtn: {
    width: '34px', height: '34px', borderRadius: '10px',
    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    border: 'none', color: '#fff', fontSize: '16px',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'opacity 0.2s',
  },
};